import { I18n } from '@aws-amplify/core';
import { Col, Form, InputNumber, Row, Skeleton } from 'antd';
import { CheckboxOptionType } from 'antd/es/checkbox';
import React, { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { useStepKeysContext } from '../DefaultSelects/context';
import { AditionalItemProps } from './types';
import { fieldsUnitTexts } from './utils/FieldsUnitTexts';
import { Text } from '@/components/Typography';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useGetAdditionalItemsOptions } from '@/hooks';
import type { AdditionalItemsResults, GetAdditionalItemOptionsResponse } from '@/hooks';

import * as S from '@/components/views/EwaJdsD86/Report/styles';

const { useWatch, useFormInstance } = Form;
const { formatLabelWithUnit, formatPlaceholderWithUnit } = fieldsUnitTexts;

export function RadioAndInput({ additionalItem }: Readonly<AditionalItemProps>): JSX.Element {
	const { id, title_1, unit_1, title_2, unit_2 } = additionalItem;

	const form = useFormInstance();
	const { formFieldName, stepKey } = useStepKeysContext();
	const { organization, company } = useApplicationContext();

	const handlingOptionId = useWatch([...formFieldName, 'additional_items', id, 'additional_item_option_id_1']);
	const weightValue = useWatch([...formFieldName, 'additional_items', id, 'value_1']);

	const { data: options, isLoading: isGettingOptions } = useGetAdditionalItemsOptions({
		company_id: company?.id,
		organization_id: organization?.id,
		custom_report_step_key_id: stepKey.id
	});

	const radioLabel = formatLabelWithUnit(title_1, unit_1);

	useEffect(() => {
		const result: AdditionalItemsResults[] = [
			{
				id: uuidv4(),
				value_1: weightValue,
				additional_item_option_id_1: handlingOptionId,
				result: 0
			}
		];

		form.setFieldValue([...formFieldName, 'additional_items', id, 'results'], result);
	}, [handlingOptionId, weightValue]);

	function handleOptions(data: GetAdditionalItemOptionsResponse[]): CheckboxOptionType[] {
		return data?.map(({ description, id }) => ({
			value: id,
			label: I18n.get(`${description}`)
		}));
	}

	if (isGettingOptions) {
		return (
			<Row gutter={[0, 15]}>
				<Col span={18}>
					<Skeleton active />
				</Col>
				<Col span={16}>
					<Skeleton active paragraph />
				</Col>
			</Row>
		);
	}

	return (
		<Row gutter={[0, 15]} key={id}>
			<Col span={18}>
				<Text strong>{radioLabel}</Text>
			</Col>
			<Col span={16}>
				<Form.Item name={[...formFieldName, 'additional_items', id, 'additional_item_option_id_1']}>
					<S.CustomRadioGroup options={handleOptions(options)} />
				</Form.Item>
			</Col>
			<Col span={10}>
				<Row gutter={[0, 5]}>
					<Col span={24}>
						<Text strong>{title_2}</Text>
					</Col>
					<Col span={24}>
						<Form.Item style={{ margin: 0 }} name={[...formFieldName, 'additional_items', id, 'value_1']}>
							<InputNumber
								min={0}
								style={{ width: '100%' }}
								placeholder={formatPlaceholderWithUnit('0', unit_2)}
							/>
						</Form.Item>
					</Col>
				</Row>
			</Col>
		</Row>
	);
}
